/**
 * Servizio per mappare i template email alle proprietà delle notifiche
 * Type, Title e Action vengono dedotti dal template invece che salvati nel DB
 */

import { NotificationType } from '@/types/notification.types';

export interface NotificationMapping {
  type: NotificationType;
  title: string;
  action?: {
    type: 'redirect' | 'execute_function' | 'open_modal';
    payload: any;
  };
}

/**
 * Mappa un template email alle proprietà della notifica
 * Restituisce type, title e action basandosi sul template
 */
export class NotificationTemplateMapper {
  private static templateMap: Record<string, (payload: any) => NotificationMapping> = {
    // ========== ITINERARY STATUS CHANGES ==========
    'update_itinerary_from_draft_to_pending': (payload) => ({
      type: 'info',
      title: 'Itinerary Submitted',
      action: {
        type: 'redirect',
        payload: { path: '/itinerary-builder', params: { itineraryId: payload?.itinerary_id } }
      }
    }),
    
    'update_itinerary_from_pending_to_approved': (payload) => ({
      type: 'success',
      title: 'Itinerary Approved',
      action: {
        type: 'redirect',
        payload: { path: '/itinerary-builder', params: { itineraryId: payload?.itinerary_id } }
      }
    }),
    
    'update_itinerary_from_pending_to_rejected': (payload) => ({
      type: 'error',
      title: 'Itinerary Rejected',
      action: {
        type: 'redirect',
        payload: { path: '/itinerary-builder', params: { itineraryId: payload?.itinerary_id } }
      }
    }),
    
    'update_itinerarys_from_sended_to_completed': (payload) => ({
      type: 'success',
      title: 'Itinerary Completed',
      action: {
        type: 'redirect',
        payload: { path: '/itinerary-builder', params: { itineraryId: payload?.itinerary_id } }
      }
    }),

    // ========== TRAVEL REQUEST ==========
    'receved_new_travel_request': (payload) => ({
      type: 'info',
      title: 'New Travel Request',
      action: {
        type: 'redirect',
        payload: { path: '/dashboard', params: { tab: 'requests', requestId: payload?.travel_request_id } }
      }
    }),
    
    'assign_request': (payload) => ({
      type: 'info',
      title: 'Request Assigned',
      action: {
        type: 'redirect',
        payload: { path: '/dashboard', params: { tab: 'requests', requestId: payload?.travel_request_id } }
      }
    }),
    
    'travel_request_accepted_by_designer': (payload) => ({
      type: 'success',
      title: 'Request Accepted',
      action: {
        type: 'redirect',
        payload: { path: '/dashboard', params: { tab: 'requests', requestId: payload?.travel_request_id } }
      }
    }),
    
    'travel_request_rejected_by_designer': (payload) => ({
      type: 'warning',
      title: 'Request Declined',
      action: {
        type: 'redirect',
        payload: { path: '/dashboard', params: { tab: 'requests', requestId: payload?.travel_request_id } }
      }
    }),

    // ========== TRAVEL DESIGNER STATUS ==========
    'approve_new_travel_designer': (payload) => ({
      type: 'success',
      title: 'Account Approved',
      action: undefined // Nessuna action specifica
    }),
    
    'reject_new_travel_designer': (payload) => ({
      type: 'error',
      title: 'Account Rejected',
      action: undefined
    }),
    
    'travel_designer_request': (payload) => ({
      type: 'info',
      title: 'New Designer Request',
      action: {
        type: 'redirect',
        payload: { path: '/users', params: { userId: payload?.td_id } }
      }
    }),

    // ========== PAYMENTS ==========
    'notify_payment_received': (payload) => ({
      type: 'success',
      title: 'Payment Received',
      action: {
        type: 'redirect',
        payload: { path: '/payments', params: { paymentId: payload?.payment_id } }
      }
    }),
    
    'payment_request_to_td': (payload) => ({
      type: 'info',
      title: 'Payment Request',
      action: {
        type: 'redirect',
        payload: { path: '/payments', params: { paymentId: payload?.payment_id } }
      }
    }),

    'payment_failed': (payload) => ({
      type: 'error',
      title: 'Payment Failed',
      action: {
        type: 'redirect',
        payload: { path: '/payments', params: { paymentId: payload?.payment_id } }
      }
    }),

    'payment_refunded': (payload) => ({
      type: 'warning',
      title: 'Payment Refunded',
      action: {
        type: 'redirect',
        payload: { path: '/payments', params: { paymentId: payload?.payment_id } }
      }
    }),

    'payment_disputed': (payload) => ({
      type: 'warning',
      title: 'Payment Disputed',
      action: {
        type: 'redirect',
        payload: { path: '/payments', params: { paymentId: payload?.payment_id } }
      }
    }),

    // ========== FALLBACK ==========
    'default': (payload) => ({
      type: 'info',
      title: 'New Notification',
      action: undefined
    })
  };

  /**
   * Mappa un template a NotificationMapping
   */
  static mapTemplate(template: string | null, payload: any = {}): NotificationMapping {
    if (!template) {
      return this.templateMap['default'](payload);
    }

    const mapper = this.templateMap[template];
    if (!mapper) {
      console.warn(`[NotificationTemplateMapper] Template non trovato: ${template}, uso default`);
      return this.templateMap['default'](payload);
    }

    return mapper(payload);
  }

  /**
   * Estrae il messaggio dal payload
   * Cerca nei campi comuni delle variabili email
   */
  static extractMessage(template: string | null, payload: any = {}): string {
    // Se c'è un campo message esplicito, usalo
    if (payload?.message) {
      return payload.message;
    }

    // Altrimenti costruisci un messaggio basato sul template
    const variables = payload?.variables || payload;

    switch (template) {
      case 'update_itinerary_from_draft_to_pending':
        return `Itinerary "${variables?.itinerary_title || 'Unknown'}" submitted for approval`;
      
      case 'update_itinerary_from_pending_to_approved':
        return `Itinerary "${variables?.itinerary_title || 'Unknown'}" has been approved`;
      
      case 'update_itinerary_from_pending_to_rejected':
        return `Itinerary "${variables?.itinerary_title || 'Unknown'}" was rejected`;
      
      case 'update_itinerarys_from_sended_to_completed':
        return `Itinerary "${variables?.itinerary_title || 'Unknown'}" completed by client`;
      
      case 'receved_new_travel_request':
        return `New travel request from ${variables?.client_first_name || 'client'}`;
      
      case 'assign_request':
        return `You have been assigned to travel request #${variables?.travel_request_id || 'Unknown'}`;
      
      case 'travel_request_accepted_by_designer':
        return `${variables?.td_first_name || 'Designer'} accepted your travel request`;
      
      case 'travel_request_rejected_by_designer':
        return `${variables?.td_first_name || 'Designer'} declined your travel request`;
      
      case 'approve_new_travel_designer':
        return 'Your travel advisor account has been approved';
      
      case 'reject_new_travel_designer':
        return 'Your travel advisor account application was rejected';
      
      case 'travel_designer_request':
        return `${variables?.td_first_name || 'Someone'} ${variables?.td_last_name || ''} requested travel advisor status`;
      
      case 'notify_payment_received':
        return `Payment received for itinerary "${variables?.itinerary_title || 'Unknown'}"`;
      
      case 'payment_request_to_td':
        return `Payment request for trip "${variables?.itinerary_title || 'Unknown'}"`;

      case 'payment_failed':
        return `Payment of ${variables?.amount || ''} from ${variables?.client_first_name || 'client'} failed`;

      case 'payment_refunded':
        return `Payment of ${variables?.amount || ''} from ${variables?.client_first_name || 'client'} was refunded`;

      case 'payment_disputed':
        return `Payment of ${variables?.amount || ''} from ${variables?.client_first_name || 'client'} was disputed`;

      default:
        return 'You have a new notification';
    }
  }
}
